import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken, refreshAccessToken } from '@/lib/tokenRefresh'
import type { AirAlert, AirFrame, AirHealth, AirLayer, AirStats, LiveAircraft } from '@/types/airTraffic'

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]
const TRAIL_MAX = 80

export type BBox = [number, number, number, number] // west, south, east, north

export interface AirLive {
  /** hex -> latest record from the server (positions are as reported, not dead-reckoned) */
  aircraft: React.MutableRefObject<Map<string, LiveAircraft>>
  trails: React.MutableRefObject<Map<string, [number, number][]>>
  /** bumps on every frame so consumers can re-derive */
  version: number
  stats: AirStats | null
  health: AirHealth | null
  area: AirFrame['area'] | null
  watched: Set<string>
  connected: boolean
  lastFrameAt: number | null
  setView: (bbox: BBox, layers: AirLayer[]) => void
}

/** Live air picture over /api/air-traffic/ws. The token goes in the first
 * message (browsers can't set headers on a WebSocket, and a query-string
 * token would end up in proxy logs). */
export default function useAirLive(onAlert?: (a: AirAlert) => void): AirLive {
  const aircraft = useRef(new Map<string, LiveAircraft>())
  const trails = useRef(new Map<string, [number, number][]>())
  const [version, setVersion] = useState(0)
  const [stats, setStats] = useState<AirStats | null>(null)
  const [health, setHealth] = useState<AirHealth | null>(null)
  const [area, setArea] = useState<AirFrame['area'] | null>(null)
  const [watched, setWatched] = useState<Set<string>>(new Set())
  const [connected, setConnected] = useState(false)
  const [lastFrameAt, setLastFrameAt] = useState<number | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const viewRef = useRef<{ bbox: BBox; layers: AirLayer[] } | null>(null)
  const readyRef = useRef(false)
  const retriesRef = useRef(0)
  const closedByUs = useRef(false)
  const onAlertRef = useRef(onAlert)
  onAlertRef.current = onAlert
  const sendTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const sendView = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !readyRef.current || !viewRef.current) return
    ws.send(JSON.stringify({ type: 'view', ...viewRef.current }))
  }, [])

  const applyFrame = useCallback((f: AirFrame) => {
    const next = new Map<string, LiveAircraft>()
    const tr = trails.current
    for (const a of f.ac) {
      next.set(a.hex, a)
      const seeded = f.trails[a.hex]
      if (seeded) {
        tr.set(a.hex, seeded.slice(-TRAIL_MAX))
        continue
      }
      const t = tr.get(a.hex)
      if (!t) {
        tr.set(a.hex, [[a.lon, a.lat]])
      } else {
        const last = t[t.length - 1]
        if (last[0] !== a.lon || last[1] !== a.lat) {
          t.push([a.lon, a.lat])
          if (t.length > TRAIL_MAX) t.shift()
        }
      }
    }
    for (const hex of tr.keys()) if (!next.has(hex)) tr.delete(hex)
    aircraft.current = next
    setStats(f.stats)
    setHealth(f.health)
    setArea(f.area)
    setWatched(new Set(f.watched))
    setLastFrameAt(Date.now())
    setVersion((v) => v + 1)
  }, [])

  useEffect(() => {
    closedByUs.current = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      const ws = new WebSocket(`${proto}://${window.location.host}/api/air-traffic/ws`)
      wsRef.current = ws
      readyRef.current = false

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'auth', token: getAccessToken() || '' }))
      }
      ws.onmessage = (ev) => {
        let msg: any
        try {
          msg = JSON.parse(ev.data)
        } catch {
          return
        }
        if (msg.type === 'ready') {
          readyRef.current = true
          retriesRef.current = 0
          setConnected(true)
          sendView()
        } else if (msg.type === 'frame') {
          applyFrame(msg as AirFrame)
        } else if (msg.type === 'alert') {
          onAlertRef.current?.(msg.alert as AirAlert)
        }
      }
      ws.onclose = async (ev) => {
        setConnected(false)
        readyRef.current = false
        if (closedByUs.current) return
        if (ev.code === 4401) {
          // expired access token: refresh once, then reconnect
          const fresh = await refreshAccessToken().catch(() => null)
          if (!fresh) return
        }
        const delay = RECONNECT_DELAYS[Math.min(retriesRef.current, RECONNECT_DELAYS.length - 1)]
        retriesRef.current += 1
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    connect()
    return () => {
      closedByUs.current = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (sendTimer.current) clearTimeout(sendTimer.current)
      wsRef.current?.close()
    }
  }, [applyFrame, sendView])

  const setView = useCallback(
    (bbox: BBox, layers: AirLayer[]) => {
      viewRef.current = { bbox, layers }
      if (sendTimer.current) clearTimeout(sendTimer.current)
      sendTimer.current = setTimeout(sendView, 250)
    },
    [sendView],
  )

  return { aircraft, trails, version, stats, health, area, watched, connected, lastFrameAt, setView }
}

/** Dead-reckon a position forward from its report time using ground speed and
 * track, so motion stays smooth between 10-120 s server updates. Capped so a
 * stale record doesn't drift off on its own. */
export function projectPosition(a: LiveAircraft, nowSec: number, maxAgeSec = 120): [number, number] {
  if (a.gnd || !a.gs || a.trk == null) return [a.lon, a.lat]
  const age = Math.min(Math.max(0, nowSec - a.pts), maxAgeSec)
  if (age <= 0) return [a.lon, a.lat]
  const distKm = (a.gs * 1.852 * age) / 3600
  const R = 6371
  const brng = (a.trk * Math.PI) / 180
  const lat1 = (a.lat * Math.PI) / 180
  const lon1 = (a.lon * Math.PI) / 180
  const d = distKm / R
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng))
  const lon2 = lon1 + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2))
  return [((((lon2 * 180) / Math.PI + 540) % 360) - 180), (lat2 * 180) / Math.PI]
}
